import * as THREE from "three";
import type { Dimension, MatrixFor, MatrixValues, Vec3 } from "@src/math/types";
import {
  createLabelSprite,
  disposeLabelSprite,
  updateLabelSprite,
} from "./label-sprite";
import { transformVector } from "./linear-transform";
import type { RenderVector } from "../render-state";

const ARROW_SHAFT_RADIUS = 0.03;
const BASIS_ARROW_Z_OFFSET = 0.035;
const VECTOR_ARROW_Z_OFFSET = 0.055;
const DIRECTION_LABEL_OFFSET = 0.2;
const LABEL_Z_OFFSET = 0.02;
const LABEL_SIZE = 0.34;
const MIN_VISIBLE_DIRECTION_LENGTH = 0.001;
const BASIS_VECTORS = [
  { label: "x", coordinates: [1, 0, 0], color: 0xff4d43 },
  { label: "y", coordinates: [0, 1, 0], color: 0x43d675 },
  { label: "z", coordinates: [0, 0, 1], color: 0x5298ff },
] as const;

interface ArrowVisual {
  group: THREE.Group;
  originCap: THREE.Mesh;
  shaft: THREE.Mesh;
  head: THREE.Mesh;
  basicMaterial: THREE.MeshBasicMaterial;
  lambertMaterial: THREE.MeshLambertMaterial;
}

interface VectorVisual {
  arrow: ArrowVisual;
  label: THREE.Sprite;
  labelText: string;
  color: number;
}

export class ArrowVisuals {
  private readonly sharedGeometries = {
    originCap: new THREE.SphereGeometry(
      1,
      16,
      8,
      0,
      Math.PI * 2,
      Math.PI / 2,
      Math.PI / 2,
    ),
    shaft: new THREE.CylinderGeometry(1, 1, 1, 16),
    head: new THREE.ConeGeometry(1, 1, 24),
  };
  private readonly basisVisuals: readonly VectorVisual[];
  private readonly vectorVisuals = new Map<string, VectorVisual>();
  private readonly scratchEndpoint = new THREE.Vector3();
  private readonly scratchDirection = new THREE.Vector3();
  private readonly arrowUpDirection = new THREE.Vector3(0, 1, 0);
  private disposed = false;

  constructor(private readonly contentRoot: THREE.Group) {
    this.basisVisuals = BASIS_VECTORS.map(({ label, color }) =>
      this.createVectorVisual(label, color, 0),
    );
  }

  updateBasis(
    dimension: Dimension,
    matrix: Readonly<MatrixValues>,
    visible: boolean,
  ): void {
    this.basisVisuals.forEach((visual, index) => {
      if (!visible || index >= dimension) {
        visual.arrow.group.visible = false;
        visual.label.visible = false;
        return;
      }
      this.updateVectorVisual(
        visual,
        transformVector(dimension, matrix, BASIS_VECTORS[index].coordinates),
        dimension,
        BASIS_ARROW_Z_OFFSET,
      );
    });
  }

  updateVectors<D extends Dimension>(
    dimension: D,
    matrix: Readonly<MatrixFor<D>>,
    vectors: readonly RenderVector<D>[],
  ): void {
    const activeIds = new Set(vectors.map(({ id }) => id));
    for (const [id, visual] of this.vectorVisuals) {
      if (activeIds.has(id)) continue;
      this.removeVectorVisual(visual);
      this.vectorVisuals.delete(id);
    }

    for (const vector of vectors) {
      let visual = this.vectorVisuals.get(vector.id);
      if (!visual) {
        visual = this.createVectorVisual(
          vector.label,
          new THREE.Color(vector.color).getHex(),
          1,
        );
        this.vectorVisuals.set(vector.id, visual);
      }
      this.updateVector(visual, vector, dimension, matrix);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.basisVisuals.forEach((visual) => {
      this.removeVectorVisual(visual);
    });
    this.vectorVisuals.forEach((visual) => {
      this.removeVectorVisual(visual);
    });
    this.vectorVisuals.clear();
    this.sharedGeometries.originCap.dispose();
    this.sharedGeometries.shaft.dispose();
    this.sharedGeometries.head.dispose();
  }

  private createVectorVisual(
    label: string,
    color: number,
    depthBias: number,
  ): VectorVisual {
    const visual = {
      arrow: this.createArrowVisual(color, depthBias),
      label: createLabelSprite(label, color, LABEL_SIZE),
      labelText: label,
      color,
    };
    this.contentRoot.add(visual.arrow.group, visual.label);
    return visual;
  }

  private updateVector<D extends Dimension>(
    visual: VectorVisual,
    vector: RenderVector<D>,
    dimension: D,
    matrix: Readonly<MatrixFor<D>>,
  ): void {
    const transformedEndpoint = transformVector(
      dimension,
      matrix,
      dimension === 2
        ? [vector.coordinates[0], vector.coordinates[1], 0]
        : [
            vector.coordinates[0],
            vector.coordinates[1],
            vector.coordinates[2] ?? 0,
          ],
    );
    const color = new THREE.Color(vector.color).getHex();
    if (visual.labelText !== vector.label || visual.color !== color) {
      updateLabelSprite(visual.label, vector.label, color);
      visual.arrow.basicMaterial.color.setHex(color);
      visual.arrow.lambertMaterial.color.setHex(color);
      visual.labelText = vector.label;
      visual.color = color;
    }
    this.updateVectorVisual(
      visual,
      transformedEndpoint,
      dimension,
      VECTOR_ARROW_Z_OFFSET,
    );
  }

  private updateVectorVisual(
    visual: VectorVisual,
    target: Vec3,
    dimension: Dimension,
    zOffset: number,
  ): void {
    this.updateArrow(visual.arrow, target, dimension, zOffset);
    this.updateDirectionLabel(visual.label, dimension, target, zOffset);
  }

  private removeVectorVisual(visual: VectorVisual): void {
    this.contentRoot.remove(visual.arrow.group, visual.label);
    disposeArrowVisual(visual.arrow);
    disposeLabelSprite(visual.label);
  }

  private createArrowVisual(color: number, depthBias: number): ArrowVisual {
    const options = {
      color,
      polygonOffset: depthBias !== 0,
      polygonOffsetFactor: -depthBias,
      polygonOffsetUnits: -depthBias,
    };
    const basicMaterial = new THREE.MeshBasicMaterial(options);
    const lambertMaterial = new THREE.MeshLambertMaterial(options);
    const group = new THREE.Group();
    const originCap = new THREE.Mesh(
      this.sharedGeometries.originCap,
      lambertMaterial,
    );
    const shaft = new THREE.Mesh(this.sharedGeometries.shaft, lambertMaterial);
    const head = new THREE.Mesh(this.sharedGeometries.head, lambertMaterial);
    group.add(originCap, shaft, head);
    group.renderOrder = depthBias;
    return {
      group,
      originCap,
      shaft,
      head,
      basicMaterial,
      lambertMaterial,
    };
  }

  private updateArrow(
    arrow: ArrowVisual,
    target: Vec3,
    dimension: Dimension,
    zOffset: number,
  ): void {
    const end = this.scratchEndpoint.fromArray(target);
    const length = end.length();
    arrow.group.visible = length >= MIN_VISIBLE_DIRECTION_LENGTH;
    if (!arrow.group.visible) return;
    const direction = this.scratchDirection.copy(end).normalize();
    const thicknessScale = Math.min(1, length / 0.25);
    const shaftRadius = ARROW_SHAFT_RADIUS * thicknessScale;
    const headLength = Math.min(ARROW_SHAFT_RADIUS * 4, length * 0.4);
    const headRadius = ARROW_SHAFT_RADIUS * 2.7 * thicknessScale;
    const shaftLength = length - headLength;
    const material =
      dimension === 2 ? arrow.basicMaterial : arrow.lambertMaterial;
    arrow.originCap.material = material;
    arrow.originCap.scale.setScalar(shaftRadius);
    arrow.shaft.material = material;
    arrow.shaft.scale.set(shaftRadius, shaftLength, shaftRadius);
    arrow.shaft.position.y = shaftLength / 2;
    arrow.head.material = material;
    arrow.head.scale.set(headRadius, headLength, headRadius);
    arrow.head.position.y = shaftLength + headLength / 2;
    arrow.group.quaternion.setFromUnitVectors(this.arrowUpDirection, direction);
    arrow.group.position.set(0, 0, dimension === 2 ? zOffset : 0);
  }

  private updateDirectionLabel(
    label: THREE.Sprite,
    dimension: Dimension,
    target: Vec3,
    zOffset: number,
  ): void {
    const endpoint = this.scratchEndpoint.fromArray(target);
    const length = endpoint.length();
    label.visible = length >= MIN_VISIBLE_DIRECTION_LENGTH;
    if (!label.visible) return;
    endpoint.setLength(length + DIRECTION_LABEL_OFFSET);
    if (dimension === 2) endpoint.z = zOffset + LABEL_Z_OFFSET;
    label.position.copy(endpoint);
  }
}

function disposeArrowVisual(arrow: ArrowVisual): void {
  arrow.basicMaterial.dispose();
  arrow.lambertMaterial.dispose();
}
